'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Copy,
  EditIcon,
  TrashIcon,
  GlobeIcon,
  LanguagesIcon,
  CheckIcon,
  XIcon,
  ArrowUpRightIcon
} from 'lucide-react'
import Flag from 'react-flagkit'
import { toast } from 'sonner'
import { useCopyToClipboard, useDeleteLocale, useLocale as useLocaleData } from '@/hooks'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { ContentDetailInfo, ContentError, DeleteConfirmationDialog } from '@/components/partials'
import { DirectionEnum } from '@/enums'

type PropsType = {
  id: string
}

const LocaleDetailSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center gap-4">
      <Skeleton className="h-8 w-16" />
      <div>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-1 h-4 w-32" />
      </div>
    </div>

    <Card>
      <CardContent className="p-6">
        <div className="flex items-center space-x-4">
          <Skeleton className="h-16 w-16 rounded-full" />
          <div>
            <Skeleton className="h-5 w-32" />
            <Skeleton className="mt-1 h-4 w-40" />
          </div>
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-20" />
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-32" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
)

const LocaleDetail = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { copyToClipboard } = useCopyToClipboard()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const { data: localeData, isLoading, error } = useLocaleData(id)
  const { mutate: deleteLocaleMutate, isPending: isDeleting } = useDeleteLocale()

  const handleDelete = () => {
    deleteLocaleMutate(id, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['locales'] })
        toast.success(t('deleted_successfully'))
        router.push('/localization/locales')
      },
      onError: error => {
        console.error('Error deleting locale:', error)
        toast.error(t('failed_to_delete'))
      }
    })
  }

  const getDirectionBadgeColor = (direction: DirectionEnum) => {
    switch (direction) {
      case DirectionEnum.LTR:
        return 'bg-blue-100 text-blue-800 hover:bg-blue-100'
      case DirectionEnum.RTL:
        return 'bg-purple-100 text-purple-800 hover:bg-purple-100'
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-100'
    }
  }

  if (isLoading) {
    return <LocaleDetailSkeleton />
  }

  if (error || !localeData) {
    return <ContentError error={error} listUrl="/localization/locales" />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="grid grid-cols-1 items-center justify-between gap-2 sm:grid-cols-2">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href="/localization/locales">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden items-center gap-2 lg:flex">{t('list')}</span>
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <LanguagesIcon className="h-6 w-6" />
              <h1 className="text-2xl font-bold">{localeData.name}</h1>
              {localeData.active ? (
                <CheckIcon className="h-5 w-5 text-green-500" />
              ) : (
                <XIcon className="h-5 w-5 text-red-500" />
              )}
            </div>
            <p className="text-muted-foreground text-sm">ID: {localeData.id}</p>
          </div>
        </div>
        <div className="flex flex-row items-center justify-end gap-2">
          <Button variant="outline" asChild>
            <Link href={`/localization/locales/${id}/edit`}>
              <EditIcon className="h-4 w-4" />
              <span className="hidden items-center gap-2 lg:flex">{t('edit_locale')}</span>
            </Link>
          </Button>
          <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)}>
            <TrashIcon className="h-4 w-4" />
            <span className="hidden items-center gap-2 lg:flex">{t('delete')}</span>
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center space-x-4">
            <div className="bg-primary/10 flex h-16 w-16 items-center justify-center rounded-full">
              <Flag country={localeData.countryCode} size={32} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold">{localeData.name}</h2>
                <Badge variant="outline" className={getDirectionBadgeColor(localeData.direction)}>
                  {localeData.direction.toUpperCase()}
                </Badge>
              </div>
              <p className="text-muted-foreground">{localeData.fullCode}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Locale Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GlobeIcon className="h-5 w-5" />
            {t('information')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div>
              <label className="text-muted-foreground text-sm font-medium">ID</label>
              <div className="flex items-center gap-2">
                <span className="text-sm">{localeData.id}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => copyToClipboard(localeData.id)}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <div>
              <label className="text-muted-foreground text-sm font-medium">{t('name')}</label>
              <div className="flex items-center gap-2">
                <span className="text-sm">{localeData.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => copyToClipboard(localeData.name)}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <div>
              <label className="text-muted-foreground text-sm font-medium">{t('code')}</label>
              <div className="flex items-center gap-2">
                <span className="text-sm">{localeData.code}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => copyToClipboard(localeData.code)}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <div>
              <label className="text-muted-foreground text-sm font-medium">{t('country_code')}</label>
              <div className="flex items-center gap-2">
                <Flag country={localeData.countryCode} size={16} />
                <span className="text-sm">{localeData.countryCode}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => copyToClipboard(localeData.countryCode)}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <div>
              <label className="text-muted-foreground text-sm font-medium">{t('full_code')}</label>
              <div className="flex items-center gap-2">
                <span className="text-sm">{localeData.fullCode}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => copyToClipboard(localeData.fullCode)}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <div>
              <label className="text-muted-foreground text-sm font-medium">{t('direction')}</label>
              <div className="flex items-center gap-2">
                <ArrowUpRightIcon className="h-4 w-4" />
                <Badge variant="outline" className={getDirectionBadgeColor(localeData.direction)}>
                  {localeData.direction.toUpperCase()}
                </Badge>
              </div>
            </div>
            <div>
              <label className="text-muted-foreground text-sm font-medium">{t('sort_order')}</label>
              <div className="text-sm">{localeData.order}</div>
            </div>
            <div>
              <label className="text-muted-foreground text-sm font-medium">{t('status')}</label>
              <div className="flex items-center gap-2">
                {localeData.active ? (
                  <>
                    <CheckIcon className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-green-600">{t('active')}</span>
                  </>
                ) : (
                  <>
                    <XIcon className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-red-600">{t('inactive')}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <Separator />
          <ContentDetailInfo entity={localeData} />
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
    </div>
  )
}

export default LocaleDetail
